import React, { useMemo, useCallback } from 'react';
import moment from 'moment';
import { DatePicker, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import range from 'lodash/range';
import { formViewSelector } from '@store/policies/schedule/selectors';
import { setTriggerAt } from '@store/policies/schedule/actions';
import { SearchInput } from '@src/components/common';
import { formModes } from '@store/policies/schedule/formReducer';
import { FieldWrapper, TimeSelectorWrapper, DatetimeWrapper } from './styled';

function disabledDate(current) {
  // Can not select dateime before today EOD or after 90 days
  return current && (current < moment() || current.diff(moment(), 'days') > 90);
}

// TODO: reuse existing function?
const filterFunc = (input, options) => {
  if (!input) {
    return options;
  }
  const result = options.filter((option) => option.text.startsWith(input));
  const values = result.map((r) => r.value);
  if (moment(input, 'HH:mm', true).isValid() && !values.includes(input)) {
    result.push({
      text: input,
      value: input,
    });
  }
  return result;
};

const TimeSelector = ({ time, onChange, disabled = false }) => {
  const options = useMemo(() => {
    const g = 15;
    const optionsArr: any = [];

    // find if same day, month, year
    const isTimeToday = time.isSame(moment(), 'day');
    let hours = moment().hours();
    const minutes = moment().minutes();

    /*
      if date selected is today's date, only show hours and minutes which are in the future
      if date is of the next day and beyond, show all possible hours and minutes
    */
    if (isTimeToday) {
      if (hours === 23) {
        // setting hours to 24 stops the first loop below from executing even once
        hours = minutes >= 45 ? 24 : hours;
      }
    } else {
      // setting hours to 0 makes it run from 0 to 23
      hours = 0;
    }

    range(hours, 24).forEach((h) => {
      range(60 / g).forEach((t) => {
        // account for a minimum of (g = 15) minute gap before the trigger time
        if (isTimeToday && h === hours && t * g <= minutes + g) {
          return;
        }
        const minute = `${t * g}`.padStart(2, '0');
        const val = `${`${h}`.padStart(2, '0')}:${minute}`;
        optionsArr.push({
          text: val,
          value: val,
        });
      });
    });

    optionsArr.push({
      text: '23:59',
      value: '23:59',
    });

    return optionsArr;
  }, [time]);

  const filterOptions = useCallback((value) => ({ data: filterFunc(value, options) }), [options]);

  return (
    <TimeSelectorWrapper>
      <SearchInput
        key={JSON.stringify(options)}
        value={{ label: time.format('HH:mm'), key: time.format('HH:mm') }}
        notFoundContent={null}
        onSearch={filterOptions}
        onChange={onChange}
        defaultData={options}
        disabled={disabled}
      />
    </TimeSelectorWrapper>
  );
};

function DateTime() {
  const dispatch = useDispatch();
  const { mode, triggerAt } = useSelector(formViewSelector);
  let defaultDate;

  if (triggerAt) {
    defaultDate = moment(triggerAt);
  } else {
    const startTime = moment().add(15, 'minutes');
    const remainder = 15 - (startTime.minutes() % 15);
    defaultDate = startTime.add(remainder, 'minutes');
    dispatch(setTriggerAt(defaultDate.format()));
  }

  const handleDateChange = (value) => {
    dispatch(setTriggerAt(value.format()));
  };

  const handleTimeChange = (value) => {
    const [hour, mins] = value.key.split(':');
    const updatedDate = moment(defaultDate).hours(hour).minutes(mins);
    dispatch(setTriggerAt(updatedDate.format()));
  };

  const disabled = mode === formModes.delete;

  return (
    <FieldWrapper>
      <DatetimeWrapper style={{ justifyContent: 'flex-end', marginTop: 5 }}>
        <Space>
          <DatePicker
            format="MM-DD-YYYY"
            disabledDate={disabledDate}
            placeholder="Select Date"
            showToday={false}
            onChange={handleDateChange}
            suffixIcon={<></>}
            defaultValue={defaultDate}
            allowClear={false}
            disabled={disabled}
          />
          <TimeSelector onChange={handleTimeChange} time={defaultDate} disabled={disabled} />
        </Space>
      </DatetimeWrapper>
    </FieldWrapper>
  );
}

export default React.memo(DateTime);
